import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';


import ContractorAuthenticationModal from '../../../../components/family-plaza/ContractorAuthenticationModal';
import PortraitPhotoRegistrationModal from '../../../../components/family-plaza/PortraitPhotoRegistrationModal';
import MemorialAlbumEditModal from '../../../../components/family-plaza/MemorialAlbumEditModal';
import { checkModalAuthenticationStatus } from '../../../../utils/authUtils';
import { getDeceasedPersonDate, getDeceasedPersonProfileText, incrementAlbumViewCount, getAlbumViewCount } from '../../../../utils/profilePhotoUtils';
import { IoEyeOutline } from "react-icons/io5";
import CyberMemorialService from '../../../../services/FamilyPlaza/CyberMemorialService';
import DeceasedPersonProfile from '../../../../components/family-plaza/DeceasedPersonProfile';
import FamilyPlazaTopSection from '../../../../components/family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from '../../../../components/family-plaza/FamilyPlazaSubHeading';

const MemorialAlbumArticleDetailsPC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [albumData, setAlbumData] = useState(null);
  const [sortedAlbumIds, setSortedAlbumIds] = useState([]);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingModal, setPendingModal] = useState(null); // Track which modal to open after auth
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('/donate/layer-1910.png');
  const handleNavigation = (route) => { navigate(route); };



  // Load all albums from API
 

  // Load album data from navigation state (primary method)
  useEffect(() => {
    const loadAlbumData = () => {
      // Try to get album data from navigation state first
      if (location.state && location.state.albumData) {
        setAlbumData(location.state.albumData);
        
        // Get sortedAlbumIds from navigation state
        if (location.state.sortedAlbumIds) {
          setSortedAlbumIds(location.state.sortedAlbumIds);
          
          // Find the index of the current album in the sortedAlbumIds array
          const currentIndex = location.state.sortedAlbumIds.findIndex(id => id === location.state.albumData.albumId);
          if (currentIndex !== -1) {
            setCurrentArticleIndex(currentIndex);
          }
        }
        setLoading(false);
       
      } else {
        // If no navigation state, redirect to the list page
        navigate('/memorial-album-board-list');
      }
    };
    loadAlbumData();
  }, [location.state, navigate]);

  // Get current article data
  const currentArticle = albumData;



  // Handle previous article
  const handlePreviousArticle = async () => {
    if (currentArticleIndex > 0) {
      const newIndex = currentArticleIndex - 1;
      const previousAlbumId = sortedAlbumIds[newIndex];
      
      if (previousAlbumId) {
        setLoading(true);
        try {
          // Fetch the previous album details from API
          const response = await CyberMemorialService.getMemorialAlbumDetail(previousAlbumId);
          
          if (response) {
            const previousAlbumData = {
              albumId: response.albumId,
              title: response.albumTitle,
              date: formatDate(response.registTime),
              image: response.photoUrl || response.photoThumbUrl,
              readCount: response.readCount,
              customerName: response.customerName,
              photoThumbUrl: response.photoThumbUrl,
              registTime: response.registTime,
              cherishId: response.cherishId,
              goinId: response.goinId
            };
            
            setCurrentArticleIndex(newIndex);
            setAlbumData(previousAlbumData);
          }
        } catch (error) {
          console.error('Error fetching previous album details:', error);
          // You could show an error message to the user here
        } finally {
          setLoading(false);
        }
      }
    }
  };

  // Handle next article
  const handleNextArticle = async () => {
    if (currentArticleIndex < sortedAlbumIds.length - 1) {
      const newIndex = currentArticleIndex + 1;
      const nextAlbumId = sortedAlbumIds[newIndex];
      
      if (nextAlbumId) {
        setLoading(true);
        try {
          // Fetch the next album details from API
          const response = await CyberMemorialService.getMemorialAlbumDetail(nextAlbumId);
          
          if (response) {
            const nextAlbumData = {
              albumId: response.albumId,
              title: response.albumTitle,
              date: formatDate(response.registTime),
              image: response.photoUrl || response.photoThumbUrl,
              readCount: response.readCount,
              customerName: response.customerName,
              photoThumbUrl: response.photoThumbUrl,
              registTime: response.registTime,
              cherishId: response.cherishId,
              goinId: response.goinId
            };
            
            setCurrentArticleIndex(newIndex);
            setAlbumData(nextAlbumData);
          }
        } catch (error) {
          console.error('Error fetching next album details:', error);
          // You could show an error message to the user here
        } finally {
          setLoading(false);
        }
      }
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };
  // Handle inventory/list navigation
  const handleInventoryList = () => {
    navigate('/memorial-album-board-list');
  };





  // Photo icon click handler
  const handlePhotoIconClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();
    
    if (isAuthenticated) {
      // If already authenticated, open photo modal directly
      setShowPhotoModal(true);
    } else {
      // If not authenticated, open auth modal first
      setPendingModal('photo');
      setShowAuthModal(true);
    }
  };

  // Authentication modal handlers
  const handleAuthConfirm = () => {
    setShowAuthModal(false);
    // Open the pending modal after successful authentication
    if (pendingModal === 'photo') {
      setShowPhotoModal(true);
    } else if (pendingModal === 'edit') {
      setShowEditModal(true);
    } else if (pendingModal === 'delete') {
      handleDeleteConfirm();
    }
    setPendingModal(null);
  };

  const handleAuthClose = () => {
    setShowAuthModal(false);
    setPendingModal(null);
  };

  // Photo registration modal handlers
  const handlePhotoConfirm = () => {
    setShowPhotoModal(false);
  };

  const handleProfilePhotoUpload = (photoUrl) => {
    console.log('Photo uploaded, updating profile photo:', photoUrl.substring(0, 50) + '...');
    // Update profile photo immediately when photo is uploaded
    setProfilePhotoUrl(photoUrl);
  };

  const handlePhotoClose = () => {
    setShowPhotoModal(false);
  };

  // Edit modal handlers
  const handleEditClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();
    
    if (isAuthenticated) {
      // If already authenticated, open edit modal directly
      setShowEditModal(true);
    } else {
      // If not authenticated, open auth modal first
      setPendingModal('edit');
      setShowAuthModal(true);
    }
  };

  const handleEditConfirm = (updatedData) => {
    // Update the current album data with the response from the API
    if (updatedData) {
      const updatedAlbumData = {
        ...albumData,
        title: updatedData.albumTitle,
        image: updatedData.photoUrl || updatedData.photoThumbUrl,
        photoThumbUrl: updatedData.photoThumbUrl,
        registTime: updatedData.registTime,
        readCount: updatedData.readCount
      };
      setAlbumData(updatedAlbumData);
    }
    setShowEditModal(false);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
  };

  // Delete functionality
  const handleDeleteClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();
    
    if (isAuthenticated) {
      // If already authenticated, show delete confirmation
      handleDeleteConfirm();
    } else {
      // If not authenticated, open auth modal first
      setPendingModal('delete');
      setShowAuthModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!albumData || !albumData.albumId) {
      console.error('No album data or albumId available for deletion');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm('정말로 이 추모앨범을 삭제하시겠습니까?\n삭제된 앨범은 복구할 수 없습니다.');
    
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await CyberMemorialService.deleteMemorialAlbumById(albumData.albumId);
      
      if (response) {
        // Show success message
        alert('앨범이 성공적으로 삭제되었습니다.');
        
        // Navigate back to the album list
        navigate('/memorial-album-board-list');
      }
    } catch (error) {
      console.error('Error deleting memorial album:', error);
      alert('앨범 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="memorial-album-article-details-pc page-memorial-album-article-details-pc announcement-list-page cyber-info-page-pc">
      
      <FamilyPlazaTopSection />
  
     <main className="main-content">
  
      <FamilyPlazaSubHeading heading="사이버추모관" />


      {/* Main Content */}
      <div className="memorial-album-article-details-main-content">
        <div className="memorial-album-article-details-page-content">
          <div className="memorial-album-article-details-container">
            
          <DeceasedPersonProfile 
              handlePhotoIconClick={handlePhotoIconClick}
            />
            {/* Action Tabs Navigation */}
            <div className="memorial-album-article-details-action-tabs">
              <div className="memorial-album-article-details-action-tabs-container">
                <ul>
                  <li><Link to="/donate">헌화하기</Link></li>
                  <li><Link to="/memorial-post-board-list">추모글</Link></li>
                  <li><Link to="/memorial-album-board-list" className="active">추모앨범</Link></li>
                </ul>
              </div>
            </div>

            {/* Content Area - Album Article Details */}
            <div className="memorial-album-article-details-content-area">
              
              {/* Loading State */}
              {loading && (
                <div className="memorial-album-article-details-loading">
                  <p>앨범을 불러오는 중...</p>
                </div>
              )}
              
              {/* Gallery Post */}
              {!loading && currentArticle && (
              <article className="memorial-album-article-details-gallery-post">
                <header className="memorial-album-article-details-post-header">
                  {/* First row: Title (left-aligned) */}
                  <div className="memorial-album-article-details-post-title-row" style={{ textAlign: 'left', marginBottom: '8px' }}>
                    <h3 className="memorial-album-article-details-post-title">
                        {currentArticle.title}
                    </h3>
                  </div>
                  
                  {/* Second row: Date (left-aligned) */}
                  <div className="memorial-album-article-details-post-date-row" style={{ textAlign: 'left', marginBottom: '8px' }}>
                    <span className="memorial-album-article-details-post-date">
                        {currentArticle.date}
                    </span>
                  </div>
                  
                  {/* Third row: View count (right-aligned) */}
                  <div className="memorial-album-article-details-post-views-row" style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    alignItems: 'center',
                    width: '102%'
                  }}>
                    <div className="memorial-album-article-details-post-views" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}><div>
                        <IoEyeOutline size={20}/>
                        </div>
                        <span className="memorial-album-article-details-post-views-count">{currentArticle.readCount}</span>
                    </div>
                  </div>
                </header>
                <div className="memorial-album-article-details-post-body">
                    <img src={currentArticle.image} alt={currentArticle.title} />
                </div>
              </article>
              )}

              {/* Post Navigation */}
              {!loading && currentArticle && (
              <nav className="memorial-album-article-details-post-navigation">
                  <div className="memorial-album-article-details-prev-next-buttons">
                  <a 
                    href="#" 
                    className={`memorial-album-article-details-btn memorial-album-article-details-btn-secondary ${currentArticleIndex === 0 ? 'disabled' : ''}`} 
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentArticleIndex > 0) {
                        handleNextArticle();
                      }
                    }}
                    style={{ opacity: currentArticleIndex === 0 ? 0.5 : 1, cursor: currentArticleIndex === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    <img src={`${process.env.PUBLIC_URL}/images61/20_227.svg`} className="memorial-album-article-details-btn-bg" alt="" />
                    <span className="memorial-album-article-details-btn-text">이전글</span>
                  </a>
                  <a 
                    href="#" 
                      className={`memorial-album-article-details-btn memorial-album-article-details-btn-secondary ${sortedAlbumIds.length > 0 && currentArticleIndex === sortedAlbumIds.length - 1 ? 'disabled' : ''}`} 
                    onClick={(e) => {
                      e.preventDefault();
                        if (sortedAlbumIds.length > 0 && currentArticleIndex < sortedAlbumIds.length - 1) {
                          handlePreviousArticle();
                        }
                    }}
                      style={{ opacity: sortedAlbumIds.length > 0 && currentArticleIndex === sortedAlbumIds.length - 1 ? 0.5 : 1, cursor: sortedAlbumIds.length > 0 && currentArticleIndex === sortedAlbumIds.length - 1 ? 'not-allowed' : 'pointer' }}
                  >
                    <img src={`${process.env.PUBLIC_URL}/images61/20_230.svg`} className="memorial-album-article-details-btn-bg" alt="" />
                    <span className="memorial-album-article-details-btn-text">다음글</span>
                  </a>
                </div>
                <div className="memorial-album-article-details-serach-btn-container">
                <button 
                  className="memorial-album-article-details-serach-btn"
                  onClick={handleEditClick}
                >
                 수정</button>
                <button 
                  className="memorial-album-article-details-serach-btn"
                  onClick={handleDeleteClick}
                >
                삭제</button>
                </div>
                <a href="#" className="memorial-album-article-details-btn memorial-album-article-details-btn-primary" onClick={(e) => {
                  e.preventDefault();
                  handleInventoryList();
                }}>
                  <img src={`${process.env.PUBLIC_URL}/images61/20_230.svg`} className="memorial-album-article-details-btn-bg" alt="" />
                  <span className="memorial-album-article-details-btn-text">목록</span>
                </a>
              </nav>
              )}
              
            </div>

          </div>
        </div>
      </div>

      

      {/* Authentication Modal */}
      <ContractorAuthenticationModal
        isOpen={showAuthModal}
        onConfirm={handleAuthConfirm}
        onClose={handleAuthClose}
      />

      {/* Portrait Photo Registration Modal */}
      <PortraitPhotoRegistrationModal
        isOpen={showPhotoModal}
        onConfirm={handlePhotoConfirm}
        onClose={handlePhotoClose}
        onPhotoUpload={handleProfilePhotoUpload}
      />

      {/* Memorial Album Edit Modal */}
      <MemorialAlbumEditModal
        isOpen={showEditModal}
        onConfirm={handleEditConfirm}
        onClose={handleEditClose}
        albumData={albumData}
      />
      </main>
    </div>
  );
};

export default MemorialAlbumArticleDetailsPC;